import { yupResolver } from "@hookform/resolvers/yup";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Box, Button, Grid, Tooltip, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useTranslation } from "react-i18next";
import ControlledTextFieldSeparatedLabel from "../../../../components/controlled/ControlledTextFieldSeparatedLabel";
import { useUpdateTransIntegrationMutation } from "../../integrationsApiSlice";
import { getFormSchema } from "./loginFormSchema";

export const TransLoginForm = ({ status }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [updateTransIntegration, { isLoading }] =
		useUpdateTransIntegrationMutation();

	// if connection status is true -> lock edit
	const [lockEdit, setLockEdit] = useState(status);

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors },
		setValue,
		setError,
	} = useForm({
		defaultValues: {
			transLogin: "",
			transPassword: "",
		},
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				if (err.message) {
					enqueueSnackbar(err.message, {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
		}
	}, [errors, enqueueSnackbar]);

	const onSubmit = async ({ transLogin, transPassword }) => {
		await updateTransIntegration({
			transLogin,
			transPassword,
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("updated_trans_integration"), {
					variant: "success",
				});
				setLockEdit(true);
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	const handleLockChange = () => {
		setLockEdit(false);
	};

	return (
		<Box
			component="form"
			autoComplete="off"
			noValidate
			onSubmit={handleSubmit(onSubmit)}
			sx={{ p: 0, position: "relative" }}
		>
			{lockEdit && (
				<Box sx={{ position: "absolute", right: "-100px" }}>
					<Tooltip
						title={
							<Typography variant="p" sx={{ fontSize: "9pt" }} component="span">
								{t("unlock_fields")}
							</Typography>
						}
						placement="top"
					>
						<Button type="button" onClick={handleLockChange}>
							<LockOpenIcon />
						</Button>
					</Tooltip>
				</Box>
			)}
			<Grid container spacing={2}>
				<Grid size={12}>
					<ControlledTextFieldSeparatedLabel
						control={control}
						disabled={lockEdit}
						name="transLogin"
						type="text"
						label="TransID"
						error={!!errors?.transLogin}
						labelXs={6}
						fieldXs={6}
						autoComplete="off transLogin new-password"
						inputProps={{
							autoComplete: "off transLogin new-password",
						}}
					/>
				</Grid>
				<Grid size={12}>
					<ControlledTextFieldSeparatedLabel
						control={control}
						disabled={lockEdit}
						name="transPassword"
						type="password"
						label={t("password_label")}
						error={!!errors?.transPassword}
						labelXs={6}
						fieldXs={6}
						autoComplete="off transPassword new-password"
						inputProps={{
							autoComplete: "off transPassword new-password",
						}}
					/>
				</Grid>
				<Grid size={12}>
					<Box sx={{ display: "flex", justifyContent: "end" }}>
						<Button
							disabled={lockEdit}
							loading={isLoading}
							type="submit"
							variant="contained"
						>
							{t("save")}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
};
